import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { fetchListFeaturedVoices, followUser, unfollowUser } from '../../apis/discovery.api';
import CardAuthor from '../../components/DiscoveryComponent/RightContentComponent/components/card-author.component';
import { useAtomValue } from 'jotai';
import { profileAtom } from '../../components/Settings/atom';
import GoBackButton from '../../components/GoBackButton';
import { Spinner } from '@chakra-ui/react';
import { DataLayer } from '../../utils/data-layer';
import SEO from '../../components/SEO';
import { generateSEOMetadata, collectionPages } from '../../utils/seo';

const FeaturedVoicesPage = () => {
  const navigate = useNavigate();
  const [listFeaturedVoices, setListFeaturedVoices] = useState([]);
  const [loadingAuthorId, setLoadingAuthorId] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    totalPage: 0
  });
  const [loading, setLoading] = useState(false);

  const profile = useAtomValue(profileAtom);

  const handleLoadMore = () => {
    const scrollable = document.documentElement.scrollHeight - window.innerHeight;
    const scrolled = window.scrollY;
    if (scrolled >= scrollable - 300 && !loading && pagination.page < pagination.totalPage) {
      setLoading(true);
      setPagination((prevPagination) => ({
        ...prevPagination,
        page: prevPagination.page + 1
      }));
    }
  };

  const handleFollowUser = async (id, isCurrentlyFollowing) => {
    try {
      setLoadingAuthorId(id);

      // Track writer follow event when following (not unfollowing)
      if (!isCurrentlyFollowing) {
        const author = listFeaturedVoices.find((user) => user.id === id);
        if (author?.username) {
          DataLayer.trackWriterFollow(author.username);
        }
      }

      if (isCurrentlyFollowing) {
        await unfollowUser(id);
      } else {
        await followUser(id);
      }
      setListFeaturedVoices((prevResults) =>
        prevResults.map((user) =>
          user.id === id ? { ...user, isFollowing: !isCurrentlyFollowing } : user
        )
      );
    } catch (error) {
      console.error('Error updating follow status:', error);
    } finally {
      setLoadingAuthorId(null);
    }
  };

  useEffect(() => {
    window.addEventListener('scroll', handleLoadMore);
    return () => {
      window.removeEventListener('scroll', handleLoadMore);
    };
  });

  useEffect(() => {
    const getFeaturedVoices = async () => {
      setLoading(true);
      try {
        const result = await fetchListFeaturedVoices(pagination, profile.id);
        if (result) {
          setListFeaturedVoices((prevList) =>
            pagination.page === 1 ? result.data : [...prevList, ...result.data]
          );
          setPagination((prevPagination) => ({
            ...prevPagination,
            totalPage: result.totalPage
          }));
        }
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    };
    getFeaturedVoices();
  }, [pagination.page, profile.id]);

  // Generate SEO metadata for featured voices page
  const seoMetadata = generateSEOMetadata.collectionPage(
    collectionPages['featured-voices'].name,
    collectionPages['featured-voices'].url,
    collectionPages['featured-voices'].description
  );

  return (
    <div>
      <SEO {...seoMetadata} />
      <div className="flex flex-start flex-col px-4 md:px-0">
        <GoBackButton
          onClick={() => {
            navigate('/');
            window.scrollTo(0, 0);
          }}
        />
        <div className="flex sticky top-[68px] md:top-[80px] pt-3 md:pt-10 z-20 bg-white">
          <p className="text-[20px] md:text-[32px] font-[800] w-full text-left md:text-center pl-10 md:pl-0 text-growth-green-500">
            Featured Voices
          </p>
        </div>

        <div className="w-full max-w-[604px] mx-auto mt-6 md:mt-10 flex flex-col gap-5 md:gap-10">
          {listFeaturedVoices?.map((item) => (
            <CardAuthor
              key={item.id}
              author={item}
              handleFollowUser={handleFollowUser}
              isLoading={loadingAuthorId === item.id}
            />
          ))}
          {loading && (
            <div className="w-full flex justify-center py-6">
              <Spinner
                thickness="4px"
                speed="0.65s"
                emptyColor="gray.200"
                color="green.500"
                size="lg"
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FeaturedVoicesPage;
